import {
  AFTER_ONLY_OFFSET_ANCHOR_TYPES,
  ANCHOR_TYPE,
  INSTRUMENT_EVENT,
  PINNING_ANCHOR_TYPES,
  START_OR_END,
  VISIT_STATUS,
} from '../constants'

export const isSinglePointAnchor = type =>
  [
    ANCHOR_TYPE.participantStartDate,
    ANCHOR_TYPE.participantLogin,
    ANCHOR_TYPE.icfSignatureDate,
    ANCHOR_TYPE.fixedCalendarDate,
  ].includes(type)

export const hidesOffset = type => type === ANCHOR_TYPE.fixedCalendarDate

/** Signed day-count for validation. "Before" is negative and is never silently clamped. */
export const offsetDays = anchor => {
  const offset = anchor?.offset || {}
  if (offset.direction) {
    const amount = Number(offset.amount ?? offset.days ?? 0)
    return offset.direction === 'before' ? -Math.abs(amount) : amount
  }
  return Number(offset.days ?? 0)
}

export const selectedInstanceIds = anchor => {
  if (Array.isArray(anchor?.instanceIds) && anchor.instanceIds.length) return anchor.instanceIds.map(String)
  return anchor?.instanceId ? [String(anchor.instanceId)] : []
}

/** Negative offset (Before) is rejected with inline validation; never silently clamped. */
export const isNegativeOffsetDisallowed = anchor => {
  const type = anchor?.type
  if (type === ANCHOR_TYPE.icfSignatureDate) return true
  if (type === ANCHOR_TYPE.instrument) {
    return (
      anchor?.instrumentEvent === INSTRUMENT_EVENT.completion ||
      anchor?.instrumentEvent === INSTRUMENT_EVENT.missed
    )
  }
  if (type === ANCHOR_TYPE.visit) {
    return (
      anchor?.visitStatus === VISIT_STATUS.completedDate ||
      anchor?.visitStatus === VISIT_STATUS.missedDate
    )
  }
  if (AFTER_ONLY_OFFSET_ANCHOR_TYPES.includes(type)) return true
  if (type === ANCHOR_TYPE.phase) {
    return anchor?.startOrEnd !== START_OR_END.end
  }
  return false
}

export const isPinningAnchorType = type => PINNING_ANCHOR_TYPES.includes(type)

export const cascadingRecallAllowed = anchors =>
  (anchors || []).some(anchor => anchor.type === ANCHOR_TYPE.visit || anchor.type === ANCHOR_TYPE.visitCycle)

export const isChapterBasedTrack = study => {
  const cycles = study?.config?.cycles
  return Array.isArray(cycles) && cycles.length > 0
}

/** Dummy / non-cycled visit schedules — no real treatment cycle to anchor on. */
export const isDummyVisitCycle = cycle => {
  if (cycle?.is_dummy || cycle?.isDummy || cycle?.metadata?.is_dummy) return true
  const visits = cycle?.visits || []
  return visits.length === 1 && !!cycle?.name && visits[0]?.name === cycle.name
}

export const selectableVisitCycles = (entityOptions = {}) =>
  (entityOptions.cycles || []).filter(cycle => !isDummyVisitCycle(cycle))

/** True when the study visit schedule includes at least one real (non-dummy) cycle. */
export const hasSelectableVisitCycles = (entityOptions = {}) => selectableVisitCycles(entityOptions).length > 0

/** True when the study has at least one visit in the visit schedule. */
export const hasSelectableVisits = (entityOptions = {}) => (entityOptions.visits || []).length > 0

export const untilAnchoringVisitCompletedAllowed = (schedule, entityOptions = {}) =>
  hasSelectableVisits(entityOptions) && cascadingRecallAllowed(schedule?.anchors)
